/**
 * useTimelineData Hook
 *
 * Orchestrates all data fetching and transformation for the timeline.
 * Combines crop data, schedule data, grouping, and sorting into final dataset.
 */

import { useMemo } from 'react'
import { useOrgParams } from '~/common/use-org-params'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import { useMultiCropScheduleData } from '../../hooks/use-multi-crop-schedule-data'
import { useMultiCropTaskData } from '../../hooks/use-multi-crop-task-data'
import { useOrgTaskData } from '../../hooks/use-org-task-data'
import { useMultiCropSuccessionData } from '../../hooks/use-multi-crop-succession-data'
import { useCropScheduleGroups } from '../../hooks/use-crop-schedule-groups'
import { useCropScheduleSort } from '../../hooks/use-crop-schedule-sort'
import { scheduleDataToWeekFormat } from '../../utils/timeline-utils'
import { taskDataToWeekFormat } from '../../utils/timeline-task-utils'
import { orgTasksToWeekFormat, insertOrgTaskRowsIntoGroups } from '../../utils/org-task-row-utils'

/**
 * useTimelineData - Fetch and transform all timeline data
 *
 * Data flow:
 * 1. Fetch parent crops (useCropData — child crops filtered out)
 * 2. Fetch succession map (child IDs + interval_days per parent)
 * 3. Build allCrops = parents + minimal child objects
 * 4. Fetch schedule + task data for allCrops
 * 5. Transform to week-based format
 * 6. Merge: parent crop rows get succession.children enriched with actual child phases/tasks
 * 7. Group + sort + insert org task rows
 *
 * @param {Object} params
 * @param {Array} params.scheduleDataOverride - Optional data override for preview mode
 * @param {boolean} params.dayPrecision - Use day precision for accurate rendering
 * @param {Object} params.timelineDateRange - { start, end } Date objects
 * @param {number} params.totalWeeks - Number of weeks in timeline
 * @param {string} params.groupStrategy - Grouping mode
 * @param {string} params.sortStrategy - Sorting mode
 * @param {string} params.viewStrategy - 'phases' | 'tasks' | 'both'
 *
 * @returns {Object} { crops, groups, expandedGroupIds, toggleGroup, error, loading }
 */
export const useTimelineData = ({
  scheduleDataOverride,
  dayPrecision,
  timelineDateRange,
  totalWeeks,
  groupStrategy,
  sortStrategy,
  viewStrategy,
  showOrgTasks,
}) => {
  const { seasonId } = useOrgParams()

  // Fetch parent crops only (child/succession crops are filtered in useCropData)
  const { crops: rawCrops, loading: cropsLoading, error: cropsError } = useCropData()

  // Fetch succession map: Map<parentCropId, { count, children: [{id, interval_days}] }>
  const { successionMap, loading: successionLoading } = useMultiCropSuccessionData({
    crops: scheduleDataOverride ? [] : rawCrops
  })

  // Set of parent crop IDs — source-of-truth filter for rendered rows
  const rawCropIdSet = useMemo(() => new Set(rawCrops.map(c => c.id)), [rawCrops])

  // Build minimal child crop objects (id + organization_id from parent) for fetching
  const childCrops = useMemo(() => {
    const result = []
    for (const [parentId, { children }] of successionMap) {
      const parent = rawCrops.find(c => c.id === parentId)
      for (const child of children) {
        result.push({ id: child.id, organization_id: parent?.organization_id })
      }
    }
    return result
  }, [successionMap, rawCrops])

  // All crops (parents + children) — needed so schedule/task hooks fetch child data too
  const allCrops = useMemo(() => [...rawCrops, ...childCrops], [rawCrops, childCrops])

  // Fetch schedules for all crops (skip if override provided)
  // Pass seasonId so historical seasons load their own schedule data, not the current active schedule
  const { scheduleData: fetchedScheduleData, loading: scheduleLoading, error: schedulesError } = useMultiCropScheduleData({
    crops: scheduleDataOverride ? [] : allCrops,
    seasonId: scheduleDataOverride ? null : seasonId,
  })

  // Use override or fetched data
  const scheduleData = scheduleDataOverride || fetchedScheduleData

  // Fetch tasks for all crops (parents + children)
  const { taskData: fetchedTaskData, loading: taskLoading, error: taskError } = useMultiCropTaskData({
    crops: scheduleDataOverride ? [] : allCrops
  })

  // Fetch org-level tasks (skip in preview/override mode)
  const { orgTaskGroups, loading: orgTaskLoading, error: orgTaskError } = useOrgTaskData({
    skip: !!scheduleDataOverride || !showOrgTasks
  })

  // Extract tasks from override data if present
  const taskDataSource = useMemo(() => {
    if (scheduleDataOverride && scheduleDataOverride.length > 0) {
      // Extract tasks from override data
      return scheduleDataOverride
        .filter(item => item.tasks && item.tasks.length > 0)
        .map(item => ({
          crop: item.crop,
          tasks: item.tasks
        }))
    }
    return fetchedTaskData
  }, [scheduleDataOverride, fetchedTaskData])

  // Transform schedules to week-based format (all crops incl. children)
  const cropsWithPhases = useMemo(
    () => scheduleDataToWeekFormat(scheduleData, dayPrecision, timelineDateRange.start, totalWeeks),
    [scheduleData, dayPrecision, timelineDateRange.start, totalWeeks]
  )

  // Transform tasks to week-based format (all crops incl. children)
  const cropsWithTasks = useMemo(
    () => taskDataToWeekFormat(taskDataSource, dayPrecision, timelineDateRange.start, totalWeeks),
    [taskDataSource, dayPrecision, timelineDateRange.start, totalWeeks]
  )

  // Transform org task groups to timeline-ready org task rows
  // Hidden in phases-only view since org task rows have no phases
  const orgTaskRows = useMemo(() => {
    if (viewStrategy === 'phases') return []
    return orgTasksToWeekFormat(orgTaskGroups, dayPrecision, timelineDateRange.start, totalWeeks)
  }, [orgTaskGroups, viewStrategy, dayPrecision, timelineDateRange.start, totalWeeks])

  // Build id-keyed lookup maps for child phase/task data
  const phasesById = useMemo(() => {
    const map = new Map()
    for (const crop of cropsWithPhases) map.set(crop.id, crop)
    return map
  }, [cropsWithPhases])

  const tasksById = useMemo(() => {
    const map = new Map()
    for (const crop of cropsWithTasks) map.set(crop.id, crop)
    return map
  }, [cropsWithTasks])

  // Merge tasks and enriched succession into PARENT crop rows only
  // In preview/override mode, skip the DB-id filter (preview crops won't be in rawCropIdSet)
  const cropsWithMergedTasks = useMemo(() => {
    return cropsWithPhases
      .filter(crop => scheduleDataOverride || rawCropIdSet.has(crop.id))  // parent crops only (source-of-truth guard)
      .map(crop => {
        const cropTasks = tasksById.get(crop.id)
        const successionEntry = successionMap.get(crop.id)

        // Enrich each child with its actual week-format phases + tasks from DB
        const enrichedSuccession = successionEntry ? {
          ...successionEntry,
          children: successionEntry.children.map(child => ({
            ...child,
            phases: phasesById.get(child.id)?.phases || [],
            tasks: tasksById.get(child.id)?.tasks || [],
          }))
        } : null

        return {
          ...crop,
          tasks: cropTasks?.tasks || [],
          succession: enrichedSuccession,
        }
      })
  }, [cropsWithPhases, tasksById, successionMap, rawCropIdSet, phasesById, scheduleDataOverride])

  // Flat crops array for SVG rendering (org task rows appended; order irrelevant — Y pos from groups)
  const crops = useMemo(() => {
    return [...cropsWithMergedTasks, ...orgTaskRows]
  }, [cropsWithMergedTasks, orgTaskRows])

  // Group crops by strategy (crop rows only — org task rows inserted separately)
  const { groups: unsortedGroups, expandedGroupIds, toggleGroup } = useCropScheduleGroups({
    crops: cropsWithMergedTasks,
    groupStrategy
  })

  // Sort groups by strategy
  const sortedGroups = useCropScheduleSort({ groups: unsortedGroups, sortStrategy })

  // Insert org task rows into correct position within groups
  const groups = useMemo(
    () => insertOrgTaskRowsIntoGroups(sortedGroups, orgTaskRows, groupStrategy, sortStrategy),
    [sortedGroups, orgTaskRows, groupStrategy, sortStrategy],
  )

  // Aggregate errors
  const error = !scheduleDataOverride && (
    cropsError || schedulesError || taskError || orgTaskError
  )

  // Loading state
  const loading = (
    cropsLoading
    || scheduleLoading
    || taskLoading
    || orgTaskLoading
    || successionLoading
    // || !cropsWithMergedTasks
    // || cropsWithMergedTasks.length === 0
  )

  return {
    crops,
    groups,
    expandedGroupIds,
    toggleGroup,
    error,
    loading,
    successionMap,
  }
}
